'use client';

import { useMemo, type PropsWithChildren } from 'react';

import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';

const syncStoragePersister = createSyncStoragePersister({
  storage: typeof window !== 'undefined' ? window.sessionStorage : undefined,
});

function QueryProvider({ children }: PropsWithChildren) {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: false,
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: false, // 네트워크 재연결 시에도 패칭하지 않음
          },
        },
      }),
    [],
  );

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: syncStoragePersister,
        maxAge: 1000 * 60 * 30, // 최대 30분
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            return !!query.options.meta?.persist;
          }, // persist 옵션이 true인 경우에만 캐싱
        },
      }}>
      <QueryClientProvider client={queryClient}>
        {children}
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      </QueryClientProvider>
    </PersistQueryClientProvider>
  );
}

export default QueryProvider;
